import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MessageCircle, ShoppingCart, MapPin, ShieldCheck, Star, AlertTriangle, X, Send, User, Mail, Phone } from 'lucide-react';

const NODE_API_URL = '/nodejs';

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [product, setProduct] = useState<any>(null);
    const [activeImage, setActiveImage] = useState('');
    const [similarProducts, setSimilarProducts] = useState<any[]>([]); 
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string | null>(null);

    // --- NEW: Inquiry Modal State ---
    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [inquiryForm, setInquiryForm] = useState({
        name: '',
        email: '',
        mobile: '',
        message: '',
        quantity: ''
    });
    const [sendingInquiry, setSendingInquiry] = useState(false);

    // --- EXISTING: Fetch Product Details ---
    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${NODE_API_URL}/api/products/${id}`);
                if (res.ok) {
                    const data = await res.json();
                    setProduct(data);
                    if(data.images && data.images.length > 0) setActiveImage(data.images[0]);
                    if (data.categoryId) fetchSimilarProducts(data.categoryId, data.id);
                    
                    // Set default quantity if available
                    setInquiryForm(prev => ({ ...prev, quantity: data.moq || '1' }));
                }
            } catch (err) { console.error(err); } 
            finally { setLoading(false); }
        };

        if (id) fetchDetails();
        window.scrollTo(0, 0); 
    }, [id]);

    // --- NEW ADDITION: Add to Buyer Activity History (Track Views) ---
    // Ye code backend ko batayega ki user ne ye product dekha hai
    useEffect(() => {
        const trackHistory = async () => {
            // User logged in check
            const userStr = localStorage.getItem('currentUser');
            const user = userStr ? JSON.parse(userStr) : null;

            if (user && user.id && id) {
                try {
                    await fetch(`${NODE_API_URL}/api/history/add`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            buyerId: user.id,
                            productId: id
                        })
                    });
                    // Success hone par console me dikhega, user ko disturb nahi karenge
                    console.log("Product view saved to history");
                } catch (err) {
                    console.error("Failed to save history", err);
                }
            }
        };

        trackHistory();
    }, [id]); // Jab bhi naya Product ID aayega, ye chalega

    const fetchSimilarProducts = async (catId: number, currentId: number) => {
        try {
            const res = await fetch(`${NODE_API_URL}/api/products/category/${catId}`);
            if (res.ok) {
                const data = await res.json();
                const others = (data.products || []).filter((p: any) => p.id !== currentId).slice(0, 4); 
                setSimilarProducts(others);
            }
        } catch (e) { console.error(e); }
    };

    // --- NEW: Handle Open Inquiry Modal ---
    const handleOpenInquiry = () => {
        const userStr = localStorage.getItem('currentUser');
        const user = userStr ? JSON.parse(userStr) : null;

        if (user) {
            // Auto-fill details for logged-in user
            setInquiryForm({
                name: user.name || user.companyName || '',
                email: user.email || '',
                mobile: user.mobile || '',
                message: `I am interested in ${product.productName}. Please send me best price details.`,
                quantity: product.moq || '10'
            });
        } else {
            // Guest user
            setInquiryForm(prev => ({
                ...prev,
                message: `I am interested in ${product.productName}. Please send me best price details.`
            }));
        }
        setShowInquiryModal(true);
    };

    // --- NEW: Handle Submit Inquiry ---
    const handleSubmitInquiry = async (e: React.FormEvent) => {
        e.preventDefault();
        setSendingInquiry(true);
        
        const userStr = localStorage.getItem('currentUser');
        const user = userStr ? JSON.parse(userStr) : null;

        try {
            const payload = {
                buyerId: user ? user.id : null,
                sellerId: product.seller_id,
                productId: product.id,
                ...inquiryForm
            };

            const res = await fetch(`${NODE_API_URL}/api/inquiry/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                setShowInquiryModal(false);
                setMessage("Inquiry sent successfully! Seller will contact you soon.");
            } else {
                setMessage("Failed to send inquiry. Please try again.");
            }
        } catch (error) {
            console.error(error);
            setMessage("Server error.");
        } finally {
            setSendingInquiry(false);
        }
    };

    // --- EXISTING: Chat Logic (Unchanged) ---
    const handleChatStart = async () => {
        const userStr = localStorage.getItem('currentUser');
        const user = userStr ? JSON.parse(userStr) : null;
        
        if (!user || !user.id) {
            setMessage("Please login to your account to start chatting.");
            return;
        }

        if (user.userType === 'seller' && user.id === product.seller_id) {
            setMessage("You cannot chat with yourself.");
            return;
        }

        try {
            const response = await fetch(`${NODE_API_URL}/api/chat/conversation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    buyerId: user.id,
                    sellerId: product.seller_id, 
                    productId: product.id
                })
            });
            
            const data = await response.json();
            if(data.conversationId) {
                 const autoMsg = `Hi, I am interested in ${product.productName}. What is the best price for MOQ: ${product.moq} ${product.unit}?`;
                
                 await fetch(`${NODE_API_URL}/api/chat/send-message`, {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify({
                         conversationId: data.conversationId,
                         senderId: user.id,
                         senderType: 'buyer',
                         messageText: autoMsg
                     })
                 });

                navigate(`/chat/${data.conversationId}?sellerId=${product.seller_id}&companyName=${encodeURIComponent(product.companyName)}&productName=${encodeURIComponent(product.productName)}&productImage=${encodeURIComponent(activeImage)}&price=${product.price}`);
            }
        } catch (error) {
            console.error("Error starting chat:", error);
            setMessage("Server error. Please try again later.");
        }
    };

    if (loading) return <div className="min-h-screen flex justify-center items-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-900"></div></div>;
    if (!product) return <div className="p-10 text-center">Product Not Found</div>;

    return (
        <div className="bg-gray-50 min-h-screen pb-12 relative">
            
            {/* Notification Toast */}
            {message && (
                <div className="fixed bottom-10 right-10 z-[9999] animate-bounce-in">
                    <div className="bg-white border-l-4 border-blue-500 text-gray-800 px-6 py-4 rounded-lg shadow-2xl flex items-center gap-4 max-w-md">
                        <div className="bg-blue-100 p-2 rounded-full">
                            <AlertTriangle className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <p className="font-bold text-blue-600">Notification</p>
                            <p className="text-sm text-gray-600">{message}</p>
                        </div>
                        <button 
                            onClick={() => setMessage(null)} 
                            className="ml-auto text-gray-400 hover:text-gray-900 transition"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            )}

            {/* --- NEW: INQUIRY MODAL UI --- */}
            {showInquiryModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-fade-in-up">
                        <div className="bg-blue-600 p-4 flex justify-between items-center text-white">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Send size={18} /> Send Inquiry
                            </h3>
                            <button onClick={() => setShowInquiryModal(false)} className="hover:bg-blue-700 p-1 rounded-full"><X size={20}/></button>
                        </div>
                        
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-6 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <img src={activeImage} className="w-12 h-12 object-contain bg-white rounded-md border" alt="prod" />
                                <div>
                                    <p className="font-bold text-gray-800 text-sm line-clamp-1">{product.productName}</p>
                                    <p className="text-xs text-gray-500">To: {product.companyName}</p>
                                </div>
                            </div>

                            <form onSubmit={handleSubmitInquiry} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="relative">
                                        <User size={16} className="absolute left-3 top-3.5 text-gray-400" />
                                        <input type="text" placeholder="Your Name" required 
                                            value={inquiryForm.name}
                                            onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Phone size={16} className="absolute left-3 top-3.5 text-gray-400" />
                                        <input type="tel" placeholder="Mobile Number" required 
                                            value={inquiryForm.mobile}
                                            onChange={(e) => setInquiryForm({...inquiryForm, mobile: e.target.value})}
                                            className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-3.5 text-gray-400" />
                                    <input type="email" placeholder="Your Email" required 
                                        value={inquiryForm.email}
                                        onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                                        className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <div className="w-1/3">
                                        <label className="text-xs font-bold text-gray-500 mb-1 block">Quantity</label>
                                        <input type="number" required 
                                            value={inquiryForm.quantity}
                                            onChange={(e) => setInquiryForm({...inquiryForm, quantity: e.target.value})}
                                            className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div className="w-2/3">
                                        <label className="text-xs font-bold text-gray-500 mb-1 block">Unit</label>
                                        <div className="w-full px-3 py-3 bg-gray-100 text-gray-500 rounded-xl text-sm border border-gray-200">
                                            {product.unit} (MOQ: {product.moq})
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-500 mb-1 block">Message</label>
                                    <textarea rows={3} required
                                        value={inquiryForm.message}
                                        onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                                        className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                        placeholder="Describe your requirement..."
                                    ></textarea>
                                </div>

                                <button type="submit" disabled={sendingInquiry} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200 flex items-center justify-center gap-2">
                                    {sendingInquiry ? 'Sending...' : <>Send Inquiry Now <Send size={18} /></>}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 py-8">
                <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-blue-600 mb-6 flex items-center font-medium">
                    &larr; Back to Products
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white p-6 md:p-8 rounded-2xl shadow-sm mb-12">
                    <div className="flex flex-col gap-4">
                        <div className="h-[400px] border border-gray-100 rounded-xl overflow-hidden flex items-center justify-center bg-white shadow-inner relative group">
                            <img src={activeImage} alt={product.productName} className="h-full w-full object-contain p-4 transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div className="flex gap-3 overflow-x-auto py-2 px-1">
                            {product.images?.map((img: string, idx: number) => (
                                <img key={idx} src={img} alt="thumb" onClick={() => setActiveImage(img)}
                                    className={`w-20 h-20 object-contain border rounded-lg cursor-pointer transition-all ${activeImage === img ? 'border-blue-600 ring-2 ring-blue-100 scale-105' : 'border-gray-200 hover:border-blue-400'}`} 
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="mb-4">
                            <h1 className="text-3xl font-extrabold text-gray-900 leading-tight mb-2">{product.productName}</h1>
                            <div className="flex items-center gap-3">
                                <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center">IN STOCK</span>
                                <span className="text-sm text-gray-400">Product ID: #{product.id}</span>
                            </div>
                        </div>

                        <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 mb-6">
                            <div className="flex items-end gap-2 mb-1">
                                <p className="text-4xl font-bold text-gray-900">₹{product.price}</p>
                                <span className="text-gray-500 font-medium mb-1">/ {product.unit}</span>
                            </div>
                            <p className="text-sm text-gray-600">
                                Minimum Order Quantity: <span className="font-bold text-gray-800">{product.moq || product.minimum_order_quantity} {product.unit}</span>
                            </p>
                        </div>

                        <div className="mb-8">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Description</h3>
                            <p className="text-gray-600 leading-relaxed text-sm whitespace-pre-line">{product.description}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-auto">
                            {/* --- UPDATED: Send Inquiry Button --- */}
                            <button 
                                onClick={handleOpenInquiry} 
                                className="bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 flex items-center justify-center gap-2 shadow-lg shadow-blue-200 transition-all transform hover:-translate-y-0.5"
                            >
                                <ShoppingCart size={20} /> Send Inquiry
                            </button>
                            <button onClick={handleChatStart} className="border-2 border-blue-600 text-blue-600 py-3.5 rounded-xl font-bold hover:bg-blue-50 flex items-center justify-center gap-2 transition-colors">
                                <MessageCircle size={20} /> Chat with Seller
                            </button>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gray-100">
                            <p className="text-xs font-bold text-gray-400 uppercase mb-3">Sold By</p>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-xl border-2 border-white shadow-sm">
                                    {product.companyName?.charAt(0)}
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900">{product.companyName}</p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                        <MapPin size={12} /> {product.sellerCity}, {product.sellerState}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-green-600 font-medium mt-1">
                                        <ShieldCheck size={12}/> Verified Supplier
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {similarProducts.length > 0 && (
                    <div className="mt-16">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Similar Products</h2>
                            <Link to={`/categories/${product.categoryId}`} className="text-blue-600 font-medium hover:underline text-sm">
                                View All Category &rarr;
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {similarProducts.map((simProd) => (
                                <div key={simProd.id} className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col">
                                    <div className="h-48 bg-gray-100 relative overflow-hidden p-4 flex items-center justify-center">
                                        <img src={simProd.image} alt={simProd.name} className="w-full h-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-110" />
                                        <div className="absolute top-2 left-2 flex items-center bg-green-600 text-white rounded px-1.5 py-0.5 text-[10px] font-bold shadow-sm">
                                            <span className="mr-1">{simProd.rating || '4.2'}</span> <Star size={8} fill="white" />
                                        </div>
                                    </div>
                                    <div className="p-4 flex flex-col flex-grow">
                                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{simProd.name}</h3>
                                        <p className="text-xs text-gray-500 mb-2 truncate">By: {simProd.companyName}</p>
                                        <div className="mt-auto">
                                            <div className="flex items-baseline gap-2 mb-3">
                                                <p className="text-lg font-bold text-gray-900">₹{simProd.price}</p>
                                                <p className="text-xs text-green-600 font-bold">{simProd.discount || 'Best Price'}</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button className="flex items-center justify-center px-2 py-1.5 border border-gray-300 text-gray-600 rounded text-xs font-bold hover:border-blue-600 hover:text-blue-600 transition">Chat</button>
                                                <button className="flex items-center justify-center px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 transition">Inquiry</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default ProductDetailsPage;